import React from 'react';
import styled, { css } from 'styled-components';
import { useMediaQuery } from '@mui/material';

import { footerLogos } from '../helper/constant';
import { DesktopFooterLayout, MobileFooterLayout } from './FooterLayouts';
// Images
import Authenticate_Light from '../images/logos/authenticate_light.svg';
import LogoTooltip from './reusable/LogoTooltip';
import CcpaCompliant from '../images/logos/ccpa-compliant-logo.svg';
import GdprCompliant from '../images/logos/gdpr-compliant-logo.svg';
import PrivacyShield from '../images/logos/privacy-shield-framework-logo.svg';

const MainFooter = styled.footer`
    width: 100%;
    background: #0c1a68;
    color: #fff;

    @media screen and (max-width: 600px) {
        padding-bottom: 0px;
    }
`;

const FooterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 44px;

    @media screen and (max-width: 1024px) {
        grid-template-columns: 100%;
    }
`;

const GridWrapper = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 72px 26px;

    @media screen and (max-width: 820px) {
        padding: 44px 24px;
    }

    @media screen and (max-width: 500px) {
        padding: 36px 16px;
    }
`;

const LeftSideSection = styled.div`
    margin-bottom: 0;
`;

const Logo = styled.img`
    width: auto;
    height: 45px;

    ${({ brandLogo }) =>
        brandLogo &&
        css`
            width: 150px;
            height: auto;
        `};

    @media screen and (max-width: 600px) {
        ${({ brandLogo }) =>
            !brandLogo &&
            css`
                transform: scale(0.9);
            `};
    }
`;

const SubText = styled.p`
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin-top: 20px;
    color: #e5e7eb;
    width: 70%;

    @media screen and (max-width: 1024px) {
        width: 100%;
    }
`;

const sharedLinkStyle = css`
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 31.92px;
    color: #d1d5db;
    display: block;
    ${({ address }) =>
        address &&
        css`
            line-height: 19.6px;
            margin-top: ${({ marginTop }) => marginTop};
        `};

    @media only screen and (max-width: 400px) {
        font-size: 13px;
        line-height: 28px;
    }

    @media only screen and (max-width: 375px) {
        font-size: 12px;
        line-height: 26px;
    }
`;

const NavLinks = styled.a`
    ${sharedLinkStyle}
`;

const LogoContainer = styled.div`
    margin-top: 40px;
    display: flex;
    flex-wrap: wrap;
    row-gap: 40px;
    column-gap: 20px;
    width: 90%;

    @media screen and (max-width: 1024px) {
        column-gap: 24px;
        width: 100%;
    }

    @media screen and (max-width: 500px) {
        column-gap: 20px;
        row-gap: 24px;
    }
`;

const CompliantLogo = styled.img`
    transform: scale(0.99);
`;

const CopyrightText = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #eff8ff;
    margin-top: 8px;
`;

function Footer() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const isMobile = useMediaQuery('(max-width: 600px)');

    return (
        <MainFooter>
            <GridWrapper>
                <FooterGrid>
                    <LeftSideSection>
                        <Logo
                            brandLogo
                            src={Authenticate_Light}
                            alt="Authenticate.com"
                            width="auto"
                            height="auto"
                            loading="lazy"
                        />
                        <CopyrightText>
                            {`Copyright © ${currentYear} Authenticating.com LLC. All rights reserved.`}
                        </CopyrightText>
                        <SubText>
                            Disclaimer : Authenticating.com LLC d/b/a Authenticate, is not a
                            consumer reporting agency & its service does not constitute "consumer
                            reporting" as such term is defined in the federal Fair Credit Reporting
                            Act (FCRA), 15 U.S.C.A. sec 1681 et seq. The data provided from this
                            service may not be used as a factor in establishing a consumer's
                            eligibility for credit, insurance, employment or for any other purpose
                            in the FCRA & you agree not to use this service for such a purpose or to
                            take adverse action regarding any consumer application based on this
                            service.
                        </SubText>
                        <LogoContainer>
                            {footerLogos.map((logo) => (
                                <NavLinks
                                    address={true}
                                    target="_blank"
                                    rel="noreferrer"
                                    href={logo.link}
                                    key={logo.id}
                                >
                                    {logo.tooltipText ? (
                                        <LogoTooltip content={logo.tooltipText}>
                                            <Logo
                                                src={logo.logo}
                                                alt={logo.name}
                                                margin
                                                width="auto"
                                                height="auto"
                                                loading="lazy"
                                            />
                                        </LogoTooltip>
                                    ) : (
                                        <Logo
                                            src={logo.logo}
                                            alt={logo.name}
                                            margin
                                            width="auto"
                                            height="auto"
                                            loading="lazy"
                                        />
                                    )}
                                </NavLinks>
                            ))}
                            <CompliantLogo
                                src={CcpaCompliant}
                                alt="ccpa_compliant"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                            <CompliantLogo
                                src={GdprCompliant}
                                alt="gdpr_compliant"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                            <CompliantLogo
                                src={PrivacyShield}
                                alt="privacy_shield_framework"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                        </LogoContainer>
                    </LeftSideSection>
                    <>{isMobile ? <MobileFooterLayout /> : <DesktopFooterLayout />}</>
                </FooterGrid>
            </GridWrapper>
        </MainFooter>
    );
}

export default Footer;
